<template>
  <Card
    :footerDivider="false"
    :title="pukSmsResult ? $t('sms.title') : $t('letter.title')"
  >
    <template v-slot:content>
      <p>
        <span v-if="pukSmsResult && loggedIn">{{
          $t('sms.contentLoggedIn', {
            date: pukSmsResult.date,
            time: pukSmsResult.time,
            phoneNumber: pukSmsResult.phoneNumber
          })
        }}</span>
        <span v-if="pukSmsResult && !loggedIn">{{
          $t('sms.contentLoggedOut', {
            date: pukSmsResult.date,
            time: pukSmsResult.time,
            phoneNumber: pukSmsResult.phoneNumber
          })
        }}</span>
        <span v-if="!pukSmsResult">{{ $t('letter.content') }}</span>
      </p>
      <p v-if="hasGeneralError" class="error" v-dompurify-html="generalErrorMessage"></p>
      <div id="puk">
        <BasicTextfield
          @click="onPukInput"
          @input="(e) => onPukInputIsUpdated(e)"
          ref="pukInputRef"
          maxlength="11"
          autocomplete="on"
          pattern="^[A-HJ-NP-Z2-9]{5}-[A-HJ-NP-Z2-9]{5}$"
          formnovalidate="true"
          :label="$t('pukEntry.label')"
          :placeholder="$t('pukEntry.placeholder')"
          :required="true"
          :status="pukTextfieldStatus"
          :value="puk"
          inputmode="verbatim"
          @update:blur="setPukToDefaultStatus"
          @update:cleared="setPukToDefaultStatus"
          @update:error="togglePukError"
          @update:input="onPukInputIsUpdated"
          @keyup.delete="triggerPukDeletePressed"
          @keyup.enter="onSubmit"
        >
          <template v-slot:error>{{ pukErrorMessage }}</template>
          <template v-slot:info
            >{{ pukSmsResult ? $t('pukEntry.infoSms') : $t('pukEntry.infoLetter') }}
          </template>
        </BasicTextfield>
      </div>
      <div v-if="pukSmsResult" class="alert-wrapper">
        <Alert type="warning" :size="AlertSize.MEDIUM">
          <template v-slot:main>
            <div v-if="loggedIn" v-dompurify-html="$t('sms.content2LoggedIn')"></div>
            <div v-if="!loggedIn" v-dompurify-html="$t('sms.content2LoggedOut')"></div>
          </template>
        </Alert>
      </div>
    </template>
    <template v-slot:footer>
      <div class="footer-cta footer-cta--left">
        <CustomButton
          @click="onSubmit"
          :button-color="ButtonColor.PRIMARY"
          :button-type="ButtonType.BUTTON"
          :pending-state="pending"
          buttonId="submit"
          >{{ pukSmsResult ? $t('sms.cta1') : $t('letter.cta1') }}
        </CustomButton>
        <CustomButton
          v-if="!pukSmsResult"
          @click="navigateServiceCodeRequestPage"
          :button-color="ButtonColor.SECONDARY"
          :button-type="ButtonType.BUTTON"
          buttonId="redirect"
          >{{ $t('letter.cta2') }}
        </CustomButton>
        <CustomButton
          :class="[pukSmsResult ? '' : 'block']"
          @click="onBackLink"
          :button-color="ButtonColor.LINK"
          buttonId="cancel"
          >{{ pukSmsResult ? $t('sms.ctaLink') : $t('letter.ctaLink') }}
        </CustomButton>
      </div>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import {
  Alert,
  AlertSize,
  BasicTextfield,
  ButtonColor,
  ButtonType,
  Card,
  CustomButton,
  InputStatus
} from 'de-zurich-ui-library'
import { navigate } from '@/utils/NavigationUtils'
import {
  getAccountId,
  getIdLevel,
  isAuthenticated,
  renewToken
} from '@/utils/KeycloakUtils'
import { upgradeAccountIdLevel } from '@/stores/accountManagementService'
import {
  type ComponentPublicInstance,
  nextTick,
  onMounted,
  type PropType,
  ref
} from 'vue'
import { useI18n } from 'vue-i18n'
import { type PukSmsResult } from '@/types/account-management/AccountManagementTypes'
import { ResponseError } from 'account-api'

const { t } = useI18n()

const pukInputRef = ref<ComponentPublicInstance | null>(null)

const props = defineProps({
  pukSmsResult: {
    type: Object as PropType<PukSmsResult> | null,
    required: false,
    default: null
  },
  onIdLevelIsUpgraded: {
    type: Function,
    required: true
  },
  accountId: {
    type: String,
    required: false,
    default: ''
  }
})

const emit = defineEmits<{
  gotoChoice: [none: void]
}>()

const loggedIn = ref(false)
const puk = ref('')
const pukTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const pukErrorMessage = ref('')
const deletePressed = ref(false)
const hasGeneralError = ref(false)
const generalErrorMessage = ref('')
const pending = ref(false)

onMounted(async () => {
  loggedIn.value = isAuthenticated()

  if (loggedIn.value) {
    const idLevel: number | null = getIdLevel()
    switch (idLevel) {
      case 1:
        props.onIdLevelIsUpgraded()
        break
      case 0:
        break
      default:
        throw new Error('No id level available!')
    }

    if (loggedIn.value && !getAccountId()) {
      console.error('No account id available!')
    }
  }
})

const onPukInput = (): void => {
  pukTextfieldStatus.value = InputStatus.DEFAULT
}

const validatePuk = (): void => {
  if (!puk.value || puk.value === '') {
    pukTextfieldStatus.value = InputStatus.ERROR
    pukErrorMessage.value = props.pukSmsResult
      ? String(t('pukEntry.errors.emptySms'))
      : String(t('pukEntry.errors.emptyLetter'))
  } else if (!/^[A-HJ-NP-Z2-9]{5}-[A-HJ-NP-Z2-9]{5}$/.test(puk.value)) {
    pukTextfieldStatus.value = InputStatus.ERROR
    pukErrorMessage.value = String(t('pukEntry.errors.invalidFormat'))
  }
}

const triggerPukDeletePressed = (): void => {
  // Track delete key press - we'll check the exact position in onPukInputIsUpdated
  deletePressed.value = true
}

// Helper function to strip PUK code (remove dash and invalid characters)
function stripPuk(input: string): string {
  return input.replaceAll(/[^A-HJ-NP-Z2-9]/g, '').toUpperCase()
}

// Helper function to format PUK code (add dash after 5 characters)
function formatPuk(input: string): string {
  const stripped = stripPuk(input)
  if (stripped.length < 5) {
    return stripped
  }
  // Add dash after 5 characters
  return `${stripped.substring(0, 5)}-${stripped.substring(5, 10)}`
}

function resolvePukInputElement(
  candidate?: HTMLInputElement | null
): HTMLInputElement | null {
  if (candidate instanceof HTMLInputElement) {
    return candidate
  }

  const componentInstance = pukInputRef.value as ComponentPublicInstance | null
  if (!componentInstance) {
    return null
  }

  const el =
    (componentInstance.$el as HTMLElement | undefined)?.querySelector?.('input') ||
    (componentInstance as any)?.inputElement ||
    ((componentInstance.$el instanceof HTMLInputElement
      ? componentInstance.$el
      : null) as HTMLInputElement | null)

  return el instanceof HTMLInputElement ? el : null
}

interface PukInputData {
  target: HTMLInputElement | null
  selectionStart: number | null
  selectionEnd: number | null
  prevInputValue: string
  currentValue: string
}

// Extract input data from event or string value
function extractPukInputData(valOrEvent: string | Event): PukInputData {
  let target: HTMLInputElement | null = null
  let selectionStart: number | null = null
  let selectionEnd: number | null = null
  let prevInputValue: string = ''
  let currentValue: string = ''

  if (valOrEvent instanceof Event) {
    const eventTarget = (valOrEvent.target as HTMLInputElement) || null
    if (eventTarget) {
      selectionStart = eventTarget.selectionStart
      selectionEnd = eventTarget.selectionEnd
      prevInputValue = puk.value || eventTarget.value
      currentValue = eventTarget.value
    }
    target = resolvePukInputElement(eventTarget)
  } else {
    currentValue = valOrEvent
    const inputElement = resolvePukInputElement()
    if (inputElement) {
      target = inputElement
      selectionStart = inputElement.selectionStart
      selectionEnd = inputElement.selectionEnd
      prevInputValue = puk.value || inputElement.value
    }
  }

  return { target, selectionStart, selectionEnd, prevInputValue, currentValue }
}

// Check if user is deleting the dash character
function isDeletingDash(
  normalizedPrevValue: string,
  normalizedCurrentValue: string,
  limitedStripped: string
): boolean {
  if (!deletePressed.value) {
    return false
  }

  const prevHadDashAt5 =
    normalizedPrevValue.length >= 6 && normalizedPrevValue.substring(5, 6) === '-'
  const currentHasDashAt5 =
    normalizedCurrentValue.length >= 6 && normalizedCurrentValue.substring(5, 6) === '-'
  const prevStripped = stripPuk(normalizedPrevValue)

  return (
    prevHadDashAt5 &&
    !currentHasDashAt5 &&
    limitedStripped.length === prevStripped.length &&
    limitedStripped.length === 5
  )
}

// Format PUK value considering dash deletion
function formatPukValue(limitedStripped: string, wasDeletingDash: boolean): string {
  if (limitedStripped.length >= 6) {
    return formatPuk(limitedStripped)
  }

  if (limitedStripped.length === 5 && !wasDeletingDash) {
    return formatPuk(limitedStripped)
  }

  return limitedStripped
}

interface CursorPosition {
  newStart: number
  selectionLength: number
}

// Calculate new cursor position after formatting
function calculateCursorPosition(
  normalizedCurrentValue: string,
  selectionStart: number,
  selectionEnd: number | null,
  wasDeletingDash: boolean,
  maxRawLength: number
): CursorPosition {
  const textBeforeCursor = normalizedCurrentValue.slice(0, selectionStart)
  const rawBeforeCursor = stripPuk(textBeforeCursor)
  const limitedRawBeforeCursor = rawBeforeCursor.substring(0, maxRawLength)

  let formattedBeforeCursor: string
  if (wasDeletingDash && limitedRawBeforeCursor.length === 5) {
    formattedBeforeCursor = limitedRawBeforeCursor
  } else {
    formattedBeforeCursor = formatPuk(limitedRawBeforeCursor)
  }

  const newStart = formattedBeforeCursor.length
  const selectionLength =
    selectionEnd !== null && selectionEnd > selectionStart
      ? selectionEnd - selectionStart
      : 0

  return { newStart, selectionLength }
}

// Restore cursor position in input element
async function restoreCursorPosition(
  target: HTMLInputElement | null,
  formatted: string,
  newStart: number,
  selectionLength: number
): Promise<void> {
  await nextTick()

  setTimeout(() => {
    try {
      let inputElement: HTMLInputElement | null = target

      if (!(inputElement instanceof HTMLInputElement) && pukInputRef.value) {
        const componentInstance = pukInputRef.value as any
        inputElement =
          componentInstance?.$el?.querySelector?.('input') ||
          componentInstance?.inputElement ||
          (componentInstance?.$el instanceof HTMLInputElement
            ? componentInstance.$el
            : null)
      }

      if (inputElement instanceof HTMLInputElement) {
        const maxPosition = formatted.length
        const finalStart = Math.min(newStart, maxPosition)
        const finalEnd =
          selectionLength > 0
            ? Math.min(newStart + selectionLength, maxPosition)
            : finalStart

        inputElement.setSelectionRange(finalStart, finalEnd)
      }
    } catch (e) {
      console.debug('onPukInputIsUpdated setSelectionRange failed', e)
    }
  }, 0)
}

// Process PUK input with cursor position preservation
async function processPukWithCursor(
  inputData: PukInputData,
  normalizedPrevValue: string,
  normalizedCurrentValue: string,
  formatted: string,
  wasDeletingDash: boolean
): Promise<void> {
  const maxRawLength = 10
  const { target, selectionStart, selectionEnd } = inputData

  if (!target || selectionStart === null) {
    puk.value = formatted
    return
  }

  try {
    const { newStart, selectionLength } = calculateCursorPosition(
      normalizedCurrentValue,
      selectionStart,
      selectionEnd,
      wasDeletingDash,
      maxRawLength
    )

    puk.value = formatted
    await restoreCursorPosition(target, formatted, newStart, selectionLength)
  } catch (err) {
    console.debug('onPukInputIsUpdated cursor preservation failed', err)
    puk.value = formatted
  }
}

const onPukInputIsUpdated = async (valOrEvent: string | Event): Promise<void> => {
  const inputData = extractPukInputData(valOrEvent)
  const normalizedPrevValue = inputData.prevInputValue.toUpperCase()
  const normalizedCurrentValue = inputData.currentValue.toUpperCase()

  pukTextfieldStatus.value = InputStatus.VALID

  const stripped = stripPuk(normalizedCurrentValue)
  const maxRawLength = 10
  const limitedStripped = stripped.substring(0, maxRawLength)

  if (!limitedStripped && !(inputData.target && inputData.selectionStart !== null)) {
    puk.value = ''
    return
  }

  const wasDeletingDash = isDeletingDash(
    normalizedPrevValue,
    normalizedCurrentValue,
    limitedStripped
  )

  let formatted = formatPukValue(limitedStripped, wasDeletingDash)
  deletePressed.value = false

  const resolvedInputElement = resolvePukInputElement(inputData.target)
  if (resolvedInputElement) {
    resolvedInputElement.value = formatted
    inputData.target = resolvedInputElement
  }

  formatted = formatted.substring(0, 11)

  await processPukWithCursor(
    inputData,
    normalizedPrevValue,
    normalizedCurrentValue,
    formatted,
    wasDeletingDash
  )
}

const togglePukError = (): void => {
  pukTextfieldStatus.value = InputStatus.ERROR
}

const setPukToDefaultStatus = (): void => {
  pukTextfieldStatus.value = InputStatus.DEFAULT
}

const onBackLink = (): void => {
  if (props.pukSmsResult) {
    emit('gotoChoice')
  } else {
    globalThis.history.back()
  }
}

const navigateServiceCodeRequestPage = (): void => {
  navigate(
    globalThis.ENV_CONFIG?.SERVICE_LINKS.SETTINGS_PAGE.CHANGE_SERVICECODEREQUEST_PAGE
  )
}

const onSubmit = async () => {
  validatePuk()
  if (pukTextfieldStatus.value === InputStatus.ERROR) {
    return
  }

  pending.value = true
  const accountId = (loggedIn.value ? getAccountId() : props.accountId) ?? ''
  try {
    await upgradeAccountIdLevel(accountId, puk.value)
    // Success: no error thrown
    props.onIdLevelIsUpgraded()
    if (loggedIn.value) await renewToken()
  } catch (e) {
    if (e instanceof ResponseError) {
      const errorStatus: number = e.response.status
      if (errorStatus === 401) {
        navigate(globalThis.ENV_CONFIG?.REDIRECTS.LOGIN_PAGE)
      } else if (is5xxError(errorStatus)) {
        navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
      } else {
        // Business error (4xx): show generic error message
        generalErrorMessage.value = String(t('pukEntry.errors.invalidPuk'))
        hasGeneralError.value = true
      }
    } else {
      // Network or other error
      navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
    }
  } finally {
    pending.value = false
  }
}

const is5xxError = (errorStatus: number): boolean => {
  return errorStatus >= 500 && errorStatus < 600
}
</script>

<style lang="scss" scoped>
.alert-wrapper {
  margin-top: 40px;
}

:deep(.no-bold) {
  font-family: var(--f-primary-base), serif;
}
</style>
